import React, { useEffect, useMemo, useRef } from "react";
import {
  Checkbox,
  TextInput,
  Text,
  Highlight,
  Box,
  Group,
  Avatar,
  Divider,
  Loader,
  Skeleton,
  ScrollArea,
  Flex,
  Alert,
  Stack,
} from "@mantine/core";
import { IconListSearch, IconAlertCircle } from "@tabler/icons";
import { searchSelectedOrgs } from "../../Api";
import { getInitials } from "../../utils/getInitials";
import { useChildFilter } from "../../hooks/useChildFilter";
import "./CSS/FilterModal.css";
import { getOrgExtraInfoFieldsForFilter, ORG_EXTRA_INFO_FIELDS_LIMIT_FILTER_UI } from "../../utils/org-filter-config";
import { formatOrgFilterChipLabel } from "../../utils/company-filter-label";
import { CompanyFilterExtraInfoBadges } from "../../components/common/CompanyFilterExtraInfoBadges";
import { useIdeaFilters } from "../../hooks/useIdeaFilters";

interface ChildFilterPanelProps {
  searchPlaceholder?: string;
  onApply: (selectedItems: any[]) => void;
  onClear: () => void;
  selectedIds?: string[];
  searchApiFunction?: (params: { query: string; ids?: string[] }, auth0: any) => Promise<any[]>;
  loadInitialData?: boolean;
  setLocalActiveFilters?: (filters: any[] | ((prev: any[]) => any[])) => void;
  localActiveFilters?: any[];
  filterConfig?: any;
  filterName?: string;
  filterObject?: string;
  clearTrigger?: number;
  onCountUpdate?: (count: number) => void;
  ideasFiltersConfig?: { idea?: Record<string, unknown>; evidence?: Record<string, unknown> };
}

const ChildFilterPanel: React.FC<ChildFilterPanelProps> = ({
  searchPlaceholder = "Search",
  onApply,
  onClear,
  selectedIds = [],
  searchApiFunction = searchSelectedOrgs,
  loadInitialData = false,
  setLocalActiveFilters,
  localActiveFilters = [],
  filterConfig,
  filterName = 'companies',
  filterObject = 'evidence',
  clearTrigger,
  onCountUpdate,
  ideasFiltersConfig: ideasFiltersConfigProp,
}) => {
  const { ideasFiltersConfig: ideasFiltersConfigFromHook } = useIdeaFilters({
    isEvidencePage: filterObject === 'evidence',
  });
  const ideasFiltersConfig = ideasFiltersConfigProp ?? ideasFiltersConfigFromHook;
  const orgExtraInfoFields = useMemo(
    () => getOrgExtraInfoFieldsForFilter(filterConfig, ideasFiltersConfig, ORG_EXTRA_INFO_FIELDS_LIMIT_FILTER_UI),
    [filterConfig, ideasFiltersConfig]
  );

  const {
    fetchedOptions,
    loading,
    searchQuery,
    checkedItems,
    error,
    handleSearchChange,
    handleSelectionChange,
    getSelectedOptions,
    clearSelections,
  } = useChildFilter({
    searchApiFunction,
    initialSelectedIds: selectedIds,
    loadInitialData,
  });

  // Initialize with existing filter selections if available
  useEffect(() => {
    if (localActiveFilters && filterName) {
      const existingFilter = localActiveFilters.find((filter: any) => filter.name === filterName);
      if (existingFilter?.selected && existingFilter.selected.length > 0) {
        handleSelectionChange(existingFilter.selected);
      }
    }
  }, [localActiveFilters, filterName]); // Only run when these change

  // Watch for clear trigger from parent
  useEffect(() => {
    if (clearTrigger !== undefined && clearTrigger > 0) {
      clearSelections();
    }
  }, [clearTrigger, clearSelections]);

  const onCountUpdateRef = useRef(onCountUpdate);
  onCountUpdateRef.current = onCountUpdate;

  // Update parent count whenever checkedItems changes (use ref for callback to avoid infinite loop when parent re-renders)
  useEffect(() => {
    if (onCountUpdateRef.current) {
      onCountUpdateRef.current(checkedItems.length);
    }
  }, [checkedItems]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(event.target.value);
  };

  const handleOptionToggle = (optionId: string, checked: boolean) => {
    
    // Prevent double-clicking by adding a small delay check
    const currentlyChecked = checkedItems.includes(optionId);
    
    let newSelection: string[] = [];
    
    if (checked && !currentlyChecked) {
      // Add to selection if not already present
      newSelection = [...checkedItems, optionId];
      handleSelectionChange(newSelection);
    } else if (!checked && currentlyChecked) {
      // Remove from selection if currently present
      newSelection = checkedItems.filter(id => id !== optionId);
      handleSelectionChange(newSelection);
    } else {
     
      return;
    }

    // Update localActiveFilters if the function is provided
    if (setLocalActiveFilters && filterConfig) {
      updateLocalActiveFilters(newSelection);
    }
  };

  const buildOptionLabel = (option: { _id: string; name?: string; [key: string]: unknown }) =>
    orgExtraInfoFields.length > 0
      ? formatOrgFilterChipLabel(option, orgExtraInfoFields)
      : (option.name ?? option._id);

  const updateLocalActiveFilters = (selectedItems: string[]) => {
    if (!setLocalActiveFilters || !filterConfig) return;

    setLocalActiveFilters((prev: any[]) => {
      const existingIndex = prev.findIndex((f: any) => f.name === filterName);
      const existingOptions = existingIndex >= 0 ? prev[existingIndex].options ?? {} : {};

      const options = Object.fromEntries(
        selectedItems.map((id) => {
          const option = fetchedOptions.find((o) => o._id === id);
          if (option) {
            return [id, buildOptionLabel(option)];
          }
          return [id, existingOptions[id] ?? id];
        })
      );

      const filterObj = {
        ...filterConfig,
        name: filterName,
        filterObject: filterObject,
        selected: selectedItems,
        query: null,
        options,
      };

      if (existingIndex >= 0) {
        if (selectedItems.length === 0) {
          return prev.filter((_: any, i: number) => i !== existingIndex);
        }
        const updated = [...prev];
        updated[existingIndex] = filterObj;
        return updated;
      }

      return selectedItems.length > 0 ? [...prev, filterObj] : prev;
    });
  };

  const renderOptionContent = (option: any) => (
    <Stack spacing={0}>
      <Text
        size="sm"
        className="ellipsis-text"
        title={option.name}
      >
        <Highlight highlight={searchQuery}>
          {option.name}
        </Highlight>
      </Text>
      {option.email && (
        <Text size="xs" color="dimmed" className="ellipsis-text">
          <Highlight highlight={searchQuery}>
            {option.email}
          </Highlight>
        </Text>
      )}
      <CompanyFilterExtraInfoBadges org={option} extraInfoFields={orgExtraInfoFields} />
    </Stack>
  );

  const optionRowHeight = (option: any) => {
    const hasExtraInfo = orgExtraInfoFields.length > 0;
    if (hasExtraInfo) {
      return option.email ? 78 : 64;
    }

    return option.email ? 62 : 50;
  };

  const unselectedOptionRowHeight = (option: any) => {
    const hasExtraInfo = orgExtraInfoFields.length > 0;
    if (hasExtraInfo) {
      return option.email ? 68 : 54;
    }

    return option.email ? 52 : 40;
  };

  return (
    <Box w="100%" h="100%" bg="white" >

      {/* Search Input */}
      <Box>
        <TextInput
          radius="md"
          value={searchQuery}
          placeholder={searchPlaceholder}
          onChange={handleInputChange}
          icon={<IconListSearch size={14} />}
          mb={16}
          sx={{
            input: {
              height: '32px',
              minHeight: '32px',
              backgroundColor: "#F9FAFB",
              ":focus": {
                borderColor: "#b1b1b1",
              },
            },
          }}
          rightSection={loading ? <Loader size={16} /> : undefined}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" mx="md" mb="md">
          {error}
        </Alert>
      )}

      {/* Options List */}
      <ScrollArea style={{ height: "calc(100% - 180px)" }}>
        {/* Show message when no search query and no results */}
        {!loading && !searchQuery && fetchedOptions.length === 0 && !loadInitialData && (
          <Text size="sm" color="#6C757D" ta="left" mt={40}>
            Start typing to search for options
          </Text>
        )}

        <Stack spacing="xs">
          {/* Loading Skeletons */}
          {loading && fetchedOptions.length === 0 && 
            Array.from({ length: 6 }).map((_, i) => (
              <Flex key={i} align="center" gap={12} h={40}>
                <Checkbox
                  disabled
                  size={16}
                />
                <Group noWrap>
                  <Skeleton radius="xl" w={32} h={32} />
                  <Skeleton
                    height={12}
                    width={120}
                    mb={0}
                    sx={{ borderRadius: "8px" }}
                  />
                </Group>
              </Flex>
            ))
          }
          {/* Selected Items First */}
          {checkedItems.length > 0 && (
            <>
              {fetchedOptions
                .filter((option: any) => checkedItems.includes(option._id))
                .map((option: any) => (
                  <Flex 
                    key={`selected-${option._id}`} 
                    align="center" 
                    gap={12} 
                    h={optionRowHeight(option)} 
                    sx={{ cursor: 'pointer' }}
                  >
                    <Checkbox
                      checked={true}
                      onChange={(event) => {
                        handleOptionToggle(option._id, event.currentTarget.checked);
                      }}
                      size={16}
                      sx={{
                        cursor: 'pointer',
                        input: {
                          cursor: 'pointer',
                          '&:checked': {
                            backgroundColor: '#5B60E3',
                            borderColor: '#5B60E3',
                          }
                        }
                      }}
                    />
                    <Group noWrap>
                      <Avatar size="sm" radius="xl">
                        {getInitials(option.name)}
                      </Avatar>
                      <Stack spacing={0}>
                        {renderOptionContent(option)}
                      </Stack>
                    </Group>
                  </Flex>
                ))}
              <Divider my="xs" />
            </>
          )}

          {/* Unselected Items */}
          {fetchedOptions
            .filter((option: any) => 
              !checkedItems.includes(option._id) && option.name !== "All"
            )
            .map((option: any) => (
              <Flex 
                key={`unselected-${option._id}`} 
                align="center" 
                gap={12} 
                h={unselectedOptionRowHeight(option)} 
                sx={{ cursor: 'pointer' }}
              >
                <Checkbox
                  checked={false}
                  onChange={(event) => {
                    handleOptionToggle(option._id, event.currentTarget.checked);
                  }}
                  size={16}
                  sx={{
                    cursor: 'pointer',
                    input: {
                      cursor: 'pointer',
                      '&:checked': {
                        backgroundColor: '#5B60E3',
                        borderColor: '#5B60E3',
                      }
                    }
                  }}
                />
                <Group noWrap>
                  <Avatar size="sm" radius="xl">
                    {getInitials(option.name)}
                  </Avatar>
                  <Stack spacing={0}>
                    {renderOptionContent(option)}
                  </Stack>
                </Group>
              </Flex>
            ))}

          {/* No results message */}
          {!loading && searchQuery && fetchedOptions.length === 0 && (
            <Text size="sm" color="#6C757D" ta="left" mt={20}>
              No results found for "{searchQuery}"
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Box>
  );
};

export default ChildFilterPanel;
